import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Replay,
  ArrowBack,
  TrendingUp,
  TrendingDown,
  Remove,
  Refresh,
  Visibility,
  Delete,
  AutoAwesome,
  Psychology,
} from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage, ScoreChip, StatusBadge, ConfirmDialog } from './common';
import { apiService } from '../services/api';

const ScoreDiffChip = ({ diff }) => {
  if (diff == null) return <Chip label="N/A" size="small" variant="outlined" />;
  const isExact = Math.abs(diff) < 0.01;
  const isGt = diff > 0.01;
  const color = isExact ? 'success' : isGt ? 'warning' : 'info';
  const icon = isExact ? <Remove fontSize="small" /> : isGt ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />;
  return (
    <Chip
      icon={icon}
      label={isExact ? 'Exact' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}`}
      color={color}
      size="small"
      variant={isExact ? 'filled' : 'outlined'}
    />
  );
};

const EvalTypeBadge = ({ type }) => {
  if (type === 'agentic') {
    return (
      <Chip
        icon={<AutoAwesome fontSize="small" />}
        label="Agentic"
        color="secondary"
        size="small"
        variant="filled"
      />
    );
  }
  return (
    <Chip
      icon={<Psychology fontSize="small" />}
      label="Standard"
      color="default"
      size="small"
      variant="outlined"
    />
  );
};

const CandidateEvaluations = () => {
  const { candidateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [reevaluateOpen, setReevaluateOpen] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);
  const [agenticMode, setAgenticMode] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.candidates.getEvaluations(candidateId);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      setError(err.response?.data?.error || 'Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-poll while any run is in progress
  useEffect(() => {
    const hasInProgress = data?.evaluation_runs?.some(
      (r) => r.status === 'queued' || r.status === 'processing'
    );
    if (hasInProgress) {
      pollRef.current = setInterval(fetchData, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [data, fetchData]);

  const handleReevaluate = async () => {
    setReevaluating(true);
    try {
      await apiService.candidates.reevaluate(candidateId, { agentic: agenticMode });
      setReevaluateOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error re-evaluating:', err);
      alert(err.response?.data?.error || 'Failed to start re-evaluation');
    } finally {
      setReevaluating(false);
    }
  };

  const handleDeleteRun = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.submissions.delete(deleteTarget);
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting run:', err);
      alert(err.response?.data?.error || 'Failed to delete evaluation run');
    } finally {
      setDeleting(false);
    }
  };

  const groundTruthMap = {};
  let groundTruthTotal = 0;
  (data?.ground_truth || []).forEach((gt) => {
    groundTruthMap[gt.control_sum_id] = gt;
    if (gt.leadership_capability_group_name?.toUpperCase() === 'COUNTERPRODUCTIVE') {
      groundTruthTotal -= gt.score;
    } else {
      groundTruthTotal += gt.score;
    }
  });

  const calculateRunTotal = (evaluations) => {
    if (!evaluations || evaluations.length === 0) return 0;
    return evaluations.reduce((total, ev) => {
      const isCP = ev.is_counterproductive;
      const displayedScore = isCP && ev.score !== null ? -ev.score : (ev.score || 0);
      return total + displayedScore;
    }, 0);
  };

  if (loading && !data) {
    return <Loading message={`Loading evaluation history for Candidate ${candidateId}...`} />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Error loading evaluations"
        message={error}
        onRetry={() => { setError(null); fetchData(); }}
      />
    );
  }

  const runs = data?.evaluation_runs || [];

  return (
    <Box>
      <PageHeader
        title={`Candidate ${candidateId}`}
        subtitle={`${runs.length} evaluation run${runs.length !== 1 ? 's' : ''}`}
        breadcrumbs={[
          { label: 'Candidates', path: '/candidates' },
          { label: `Candidate ${candidateId}`, path: `/candidate-detail/${candidateId}` },
        ]}
        actions={[
          {
            label: 'Re-evaluate',
            icon: <Replay />,
            onClick: () => setReevaluateOpen(true),
            variant: 'contained',
            color: 'primary',
          },
          {
            label: 'Back',
            icon: <ArrowBack />,
            component: Link,
            to: '/candidates',
            variant: 'outlined',
          },
        ]}
      />

      {/* Ground Truth Summary */}
      {data?.ground_truth?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Ground Truth Scores
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Total:</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {groundTruthTotal.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">/ 80</Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell>Aspect</TableCell>
                    <TableCell align="center">Control Sum</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.ground_truth.map((gt, idx) => {
                    const isCP = gt.leadership_capability_group_name?.toUpperCase() === 'COUNTERPRODUCTIVE';
                    const displayScore = isCP ? -(gt.score || 0) : (gt.score || 0);
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {gt.leadership_capability_name}
                          </Typography>
                          {isCP && <Chip label="CP" color="error" size="small" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell align="center">{gt.control_sum_id}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700}>{displayScore.toFixed(2)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Evaluation Runs */}
      {runs.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              No evaluation runs found for this candidate.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {runs.map((run, runIdx) => {
            const runTotal = calculateRunTotal(run.evaluations);
            const isExpanded = expandedRun === runIdx;
            const isPending = run.status === 'queued' || run.status === 'processing';
            const isAgentic = run.evaluation_type === 'agentic';

            return (
              <Card
                key={run.submission_id}
                sx={{
                  borderLeft: 4,
                  borderColor: isPending
                    ? 'warning.main'
                    : isAgentic
                      ? 'secondary.main'
                      : 'primary.main',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Run {runs.length - runIdx}
                      </Typography>
                      <EvalTypeBadge type={run.evaluation_type} />
                      <StatusBadge status={run.status} />
                      <Typography variant="body2" color="text.secondary">
                        {run.created_at ? new Date(run.created_at).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {runTotal.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">/ 80</Typography>
                      <ScoreDiffChip diff={groundTruthTotal - runTotal} />
                      <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                        <IconButton size="small" onClick={() => setExpandedRun(isExpanded ? null : runIdx)}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Full Details">
                        <IconButton size="small" component={Link} to={`/candidate/${run.submission_id}`}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete this run">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteTarget(run.submission_id);
                            setDeleteOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto">
                    <Box sx={{ mt: 2 }}>
                      {run.evaluations.length === 0 ? (
                        <Typography color="text.secondary">
                          No evaluations yet. {isPending ? 'Run is still in progress...' : ''}
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell>Aspect</TableCell>
                                <TableCell align="center">Control Sum</TableCell>
                                <TableCell align="right">Ground Truth</TableCell>
                                <TableCell align="right">LLM Score</TableCell>
                                <TableCell align="center">Difference</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {run.evaluations.map((ev, evIdx) => {
                                const gt = groundTruthMap[ev.control_sum_id];
                                const gtScore = gt?.score ?? null;
                                const isCP = ev.is_counterproductive || gt?.leadership_capability_group_name?.toUpperCase() === 'COUNTERPRODUCTIVE';
                                const displayedLLMScore = isCP && ev.score !== null ? -ev.score : ev.score;
                                const displayedGTScore = isCP && gtScore !== null ? -gtScore : gtScore;
                                const diff = displayedGTScore !== null && displayedLLMScore !== null ? displayedGTScore - displayedLLMScore : null;
                                return (
                                  <TableRow key={evIdx} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>
                                        {ev.aspect_name}
                                      </Typography>
                                      {isCP && <Chip label="CP" color="error" size="small" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell align="center">{ev.control_sum_id}</TableCell>
                                    <TableCell align="right">
                                      <Typography fontWeight={700}>
                                        {gtScore !== null ? displayedGTScore.toFixed(2) : 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontWeight={700} color="primary.main">
                                        {ev.score !== null ? displayedLLMScore.toFixed(2) : 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <ScoreDiffChip diff={diff} />
                                    </TableCell>
                                    <TableCell>
                                      <StatusBadge status={ev.job_status} />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Re-evaluate Confirmation Dialog */}
      <ConfirmDialog
        open={reevaluateOpen}
        title="Re-evaluate Candidate"
        message={`Start a new evaluation run for Candidate ${candidateId}? Ground truth scores will be reused.`}
        onConfirm={handleReevaluate}
        onCancel={() => setReevaluateOpen(false)}
        confirmLabel={reevaluating ? 'Starting...' : 'Start Evaluation'}
        confirmDisabled={reevaluating}
        confirmColor="primary"
      >
        <FormControlLabel
          control={<Switch checked={agenticMode} onChange={(e) => setAgenticMode(e.target.checked)} />}
          label="Use agentic evaluation mode"
          sx={{ mt: 1 }}
        />
      </ConfirmDialog>

      {/* Delete Run Confirmation Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Evaluation Run"
        message="This will permanently delete this evaluation run and all its results. This action cannot be undone."
        onConfirm={handleDeleteRun}
        onCancel={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={deleting}
        confirmColor="error"
      />
    </Box>
  );
};

export default CandidateEvaluations;